import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Shield, Home, Car, Umbrella, Phone, Mail, MapPin, Clock, Star, ChevronRight, Award, Users, CheckCircle, Send, Loader2, BookOpen } from "lucide-react";
import axios from "axios";
import BlogList from "./components/BlogList";
import BlogPost from "./components/BlogPost";
import StateHub from "./components/StateHub";
import CityPage from "./components/CityPage";
import { hawaiiRegion } from "./data/hawaiiCities";
import { nevadaRegion } from "./data/nevadaCities";

const REGIONS = [hawaiiRegion, nevadaRegion];

const COMPARION_URL = "https://www.comparioninsurance.com/insurance-agent/california/elk-grove-0488/kenneth-carson";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Schema.org LocalBusiness structured data for AEO/GEO
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "InsuranceAgency",
  "name": "Kenneth Carson - Comparion Insurance Agent",
  "description": "Life insurance agent near me in Elk Grove CA and Natomas Sacramento. Kenneth Carson provides personalized life insurance, homeowners insurance, auto insurance, and umbrella coverage.",
  "url": "https://www.comparioninsurance.com/insurance-agent/california/elk-grove-0488/kenneth-carson",
  "telephone": "+1-916-505-1000",
  "email": "Kenneth.Carson@Comparion.com",
  "image": "https://public.libertymutual-cdn.com/entities/agents/kenneth-carson.jpg",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "9381 E Stockton Blvd, Suite 120",
    "addressLocality": "Elk Grove",
    "addressRegion": "CA",
    "postalCode": "95624",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "38.4188",
    "longitude": "-121.3789"
  },
  "areaServed": [
    {
      "@type": "City",
      "name": "Elk Grove",
      "containedInPlace": { "@type": "State", "name": "California" }
    },
    {
      "@type": "Neighborhood",
      "name": "Natomas",
      "containedInPlace": { "@type": "City", "name": "Sacramento" }
    },
    {
      "@type": "City",
      "name": "Sacramento",
      "containedInPlace": { "@type": "State", "name": "California" }
    }
  ],
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "08:30",
    "closes": "17:00"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "74"
  },
  "priceRange": "$$",
  "sameAs": [COMPARION_URL]
};

// FAQ Schema for homepage - critical for AEO
const homepageFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Who is the best life insurance agent near me in Elk Grove, CA?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kenneth Carson is a top-rated life insurance agent in Elk Grove, CA with a 5.0 rating from 74 reviews. Located at 9381 E Stockton Blvd, Suite 120, he offers personalized life insurance, auto, home, and umbrella coverage. Contact him at (916) 505-1000."
      }
    },
    {
      "@type": "Question",
      "name": "Is there a life insurance agent near me in Natomas Sacramento?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Kenneth Carson serves Natomas Sacramento residents with life insurance services. He offers virtual consultations and phone appointments for Natomas families. His office is in nearby Elk Grove, and he can be reached at (916) 505-1000 or Kenneth.Carson@Comparion.com."
      }
    },
    {
      "@type": "Question",
      "name": "Where can I find a homeowners insurance agent near me in Natomas Sacramento?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kenneth Carson provides homeowners insurance for Natomas Sacramento residents. As a Comparion Insurance agent, he helps homeowners find comprehensive coverage at competitive rates. Contact him at (916) 505-1000 for a free quote."
      }
    }
  ]
};

const Header = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#143628] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-outfit font-bold text-[#143628] text-lg">Kenneth Carson</span>
              <span className="hidden sm:inline text-[#4A6359] text-sm ml-2">| Comparion Insurance</span>
            </div>
          </Link>
          
          <nav className="hidden xl:flex items-center gap-7">
            <a href={isHomePage ? "#services" : "/#services"} className="font-manrope text-[#4A6359] hover:text-[#143628] transition-colors" data-testid="nav-services">Services</a>
            <a href={isHomePage ? "#about" : "/#about"} className="font-manrope text-[#4A6359] hover:text-[#143628] transition-colors" data-testid="nav-about">About</a>
            <a href={isHomePage ? "#areas" : "/#areas"} className="font-manrope text-[#4A6359] hover:text-[#143628] transition-colors" data-testid="nav-areas">Service Areas</a>
            {REGIONS.map((r) => (
              <Link key={r.key} to={r.basePath} className="font-manrope text-[#4A6359] hover:text-[#143628] transition-colors" data-testid={`nav-${r.key}`}>{r.stateName}</Link>
            ))}
            <Link to="/blog" className="font-manrope text-[#4A6359] hover:text-[#143628] transition-colors flex items-center gap-1" data-testid="nav-blog">
              <BookOpen className="w-4 h-4" />
              Blog
            </Link>
            <a href={isHomePage ? "#contact" : "/#contact"} className="font-manrope text-[#4A6359] hover:text-[#143628] transition-colors" data-testid="nav-contact">Contact</a>
          </nav>
          
          <a 
            href={isHomePage ? "#contact" : "/#contact"}
            className="whitespace-nowrap bg-[#C45A41] text-white px-6 py-2.5 rounded-full font-manrope font-medium hover:bg-[#A84D37] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            data-testid="header-cta-button"
          >
            Get a Quote
          </a>
        </div>
      </div>
    </header>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-screen pt-20 md:pt-24 overflow-hidden bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="relative z-10">
            <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
              Licensed Insurance Agent
            </span>
            <h1 className="font-outfit font-[850] text-4xl sm:text-5xl lg:text-6xl text-[#143628] tracking-tight leading-tight mb-6" data-testid="hero-h1">
              Expert Life Insurance Agent Near You in Elk Grove & Natomas
            </h1>
            <p className="font-manrope text-lg text-[#4A6359] leading-relaxed mb-8 max-w-xl">
              Your local Claims Adjuster turned Insurance Resource. Protect your family with customized life insurance coverage tailored to your needs in Elk Grove, CA and Natomas Sacramento.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a 
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-[#C45A41] text-white px-8 py-4 rounded-full font-manrope font-medium text-lg hover:bg-[#A84D37] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="hero-cta-primary"
              >
                Start Your Quote
                <ChevronRight className="w-5 h-5" />
              </a>
              <a 
                href="tel:+19165051000"
                className="inline-flex items-center justify-center gap-2 bg-white/50 backdrop-blur border border-[#143628]/20 text-[#143628] px-8 py-4 rounded-full font-manrope font-medium text-lg hover:bg-white transition-all duration-300"
                data-testid="hero-cta-call"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#C45A41]" />
                <span className="font-manrope text-sm text-[#4A6359]">Licensed in CA, NV, HI</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C45A41]" />
                <span className="font-manrope text-sm text-[#4A6359]">Sacramento State Alum</span>
              </div>
            </div>
          </div>
          
          {/* Right - Image & Badge */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1756982477661-107a1c72c8fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMHdhbGtpbmclMjBwYXJrJTIwc3Vuc2V0fGVufDB8fHx8MTc3NDkxOTEzNnww&ixlib=rb-4.1.0&q=85"
                alt="Happy family walking at sunset - life insurance protection"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#143628]/30 to-transparent"></div>
            </div>
            
            {/* Floating Agent Badge */}
            <div className="absolute -bottom-6 -left-6 md:left-6 backdrop-blur-xl bg-white/80 rounded-2xl p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-white/40">
              <div className="flex items-center gap-4">
                <img 
                  src="https://public.libertymutual-cdn.com/entities/agents/kenneth-carson.jpg"
                  alt="Kenneth Carson - Life Insurance Agent"
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#C45A41]"
                  data-testid="agent-photo-badge"
                />
                <div>
                  <p className="font-outfit font-bold text-[#143628]">Kenneth Carson</p>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#C45A41] text-[#C45A41]" />
                    ))}
                    <span className="font-manrope text-sm text-[#4A6359] ml-1">(74 Reviews)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBanner = () => {
  return (
    <section className="bg-[#EBEAE5] py-8">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#143628]" />
            <span className="font-manrope font-medium text-[#143628]">Comparion Executive Agent</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#143628]" />
            <span className="font-manrope font-medium text-[#143628]">Former Claims Adjuster</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#143628]" />
            <span className="font-manrope font-medium text-[#143628]">5/5 Star Rating</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://public.libertymutual-cdn.com/entities/agents/kenneth-carson.jpg"
                alt="Kenneth Carson - Your Life Insurance Expert in Elk Grove"
                className="w-full h-[400px] md:h-[500px] object-cover object-top"
                data-testid="about-agent-photo"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-[#C45A41] text-white px-6 py-3 rounded-xl font-outfit font-bold">
              10+ Years Experience
            </div>
          </div>
          
          <div>
            <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
              Meet Your Agent
            </span>
            <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight mb-6" data-testid="about-h2">
              Why Choose Kenneth Carson in Elk Grove, CA
            </h2>
            <p className="font-manrope text-[#4A6359] leading-relaxed mb-6">
              Hi, I'm Kenneth Carson, your local life insurance agent near you in Elk Grove, CA. As a former Claims Adjuster, I bring unique insights into what matters when it comes to protecting your family. My experience means I understand both sides of insurance – helping you get the right coverage and ensuring claims are handled properly.
            </p>
            <p className="font-manrope text-[#4A6359] leading-relaxed mb-8">
              Whether you're looking for a life insurance agent near me in Elk Grove CA or Natomas Sacramento, I'm here to provide personalized service and expert guidance. Visit me at my office on Stockton Blvd or reach out for a free consultation.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C45A41] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-manrope font-medium text-[#143628]">Office Location</p>
                  <p className="font-manrope text-sm text-[#4A6359]">9381 E Stockton Blvd, Ste 120<br />Elk Grove, CA 95624</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#C45A41] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-manrope font-medium text-[#143628]">Business Hours</p>
                  <p className="font-manrope text-sm text-[#4A6359]">Mon - Fri: 8:30 AM - 5:00 PM</p>
                </div>
              </div>
            </div>
            
            <a 
              href="#contact"
              className="inline-flex items-center gap-2 bg-[#143628] text-white px-8 py-4 rounded-full font-manrope font-medium hover:bg-[#1e4a38] transition-all duration-300"
              data-testid="about-cta"
            >
              Contact Me Today
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    {
      icon: Shield,
      title: "Life Insurance",
      description: "Protect your family's future with comprehensive life insurance coverage. Term, whole life, and universal options available.",
      featured: true,
      keywords: "life insurance agent near me Elk Grove CA"
    },
    {
      icon: Car,
      title: "Auto Insurance",
      description: "Get competitive rates on auto insurance with coverage that fits your needs and budget.",
      featured: false
    },
    {
      icon: Home,
      title: "Home Insurance",
      description: "Safeguard your home and belongings with customized homeowners insurance policies.",
      featured: false
    },
    {
      icon: Umbrella,
      title: "Umbrella Insurance",
      description: "Extra liability protection beyond your standard policies for complete peace of mind.",
      featured: false
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-[#EBEAE5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
            Our Coverage Options
          </span>
          <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight" data-testid="services-h2">
            Life Insurance & Comprehensive Services
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Featured Life Insurance Card */}
          <div className="md:col-span-2 md:row-span-2 bg-[#143628] rounded-3xl p-8 md:p-10 text-white relative overflow-hidden group hover:shadow-xl transition-all duration-300" data-testid="service-life-insurance">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C45A41]/10 rounded-full blur-3xl"></div>
            <Shield className="w-12 h-12 mb-6 text-[#C45A41]" />
            <h3 className="font-outfit font-bold text-2xl md:text-3xl mb-4">{services[0].title}</h3>
            <p className="font-manrope text-white/80 leading-relaxed mb-6 text-lg">
              {services[0].description}
            </p>
            <p className="font-manrope text-white/60 text-sm mb-8">
              Looking for a {services[0].keywords}? Get personalized coverage from Kenneth Carson.
            </p>
            <a 
              href="#contact"
              className="inline-flex items-center gap-2 bg-[#C45A41] text-white px-6 py-3 rounded-full font-manrope font-medium hover:bg-[#A84D37] transition-all duration-300"
              data-testid="service-life-cta"
            >
              Get Life Insurance Quote
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          
          {/* Other Service Cards */}
          {services.slice(1).map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-3xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-[#143628]/5"
              data-testid={`service-${service.title.toLowerCase().replace(' ', '-')}`}
            >
              <service.icon className="w-10 h-10 mb-4 text-[#C45A41]" />
              <h3 className="font-outfit font-bold text-xl text-[#143628] mb-3">{service.title}</h3>
              <p className="font-manrope text-[#4A6359] text-sm leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceAreas = () => {
  return (
    <section id="areas" className="py-24 md:py-32 bg-[#F7F7F5] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://images.pexels.com/photos/33385147/pexels-photo-33385147.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="California suburban neighborhood"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
            Areas We Serve
          </span>
          <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight mb-6" data-testid="areas-h2">
            Serving Elk Grove, Natomas & Sacramento Communities
          </h2>
          <p className="font-manrope text-[#4A6359] text-lg max-w-3xl mx-auto">
            Whether you live in Elk Grove, Natomas, or the greater Sacramento area, we are your local insurance advocates. As a life insurance agent near me Natomas Sacramento, Kenneth Carson provides personalized service to families throughout the region.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white/80 backdrop-blur rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300" data-testid="area-elk-grove">
            <div className="w-16 h-16 bg-[#143628] rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-outfit font-bold text-xl text-[#143628] mb-3">Elk Grove, CA</h3>
            <p className="font-manrope text-[#4A6359] text-sm mb-4">
              Our main office is located in Elk Grove at 9381 E Stockton Blvd. Visit us for in-person consultations.
            </p>
            <span className="font-manrope text-xs text-[#C45A41] font-semibold">Primary Office Location</span>
          </div>
          
          <div className="bg-white/80 backdrop-blur rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300" data-testid="area-natomas">
            <div className="w-16 h-16 bg-[#C45A41] rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-outfit font-bold text-xl text-[#143628] mb-3">Natomas, Sacramento</h3>
            <p className="font-manrope text-[#4A6359] text-sm mb-4">
              Serving Natomas residents with comprehensive life insurance and more. Virtual appointments available.
            </p>
            <span className="font-manrope text-xs text-[#C45A41] font-semibold">Virtual & Phone Consultations</span>
          </div>
          
          <div className="bg-white/80 backdrop-blur rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300" data-testid="area-sacramento">
            <div className="w-16 h-16 bg-[#143628] rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-outfit font-bold text-xl text-[#143628] mb-3">Greater Sacramento</h3>
            <p className="font-manrope text-[#4A6359] text-sm mb-4">
              Extending our services across the Sacramento metro area. Licensed in CA, NV, and HI.
            </p>
            <span className="font-manrope text-xs text-[#C45A41] font-semibold">Multi-State Licensed</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const reviews = [
    {
      name: "Sarah M.",
      location: "Elk Grove, CA",
      text: "Kenneth saved us money and explained everything clearly. Best life insurance experience we've had. He took the time to understand our family's needs.",
      rating: 5
    },
    {
      name: "Michael R.",
      location: "Natomas, Sacramento",
      text: "As a first-time homeowner, I was overwhelmed. Kenneth made getting life insurance simple and stress-free. Highly recommend!",
      rating: 5
    },
    {
      name: "Jennifer L.",
      location: "Sacramento, CA",
      text: "Professional, knowledgeable, and genuinely caring. Kenneth found us the perfect coverage at a great rate. Will refer all my friends!",
      rating: 5
    }
  ];

  return (
    <section id="reviews" className="py-24 md:py-32 bg-[#EBEAE5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
            What Our Clients Say
          </span>
          <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight mb-4" data-testid="reviews-h2">
            Client Reviews & Testimonials
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-[#C45A41] text-[#C45A41]" />
            ))}
          </div>
          <p className="font-manrope text-[#4A6359]">5.0 out of 5 based on 74 reviews</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {reviews.map((review, index) => (
            <div 
              key={index}
              className="bg-[#F7F7F5] rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#C45A41] text-[#C45A41]" />
                ))}
              </div>
              <p className="font-manrope text-[#4A6359] leading-relaxed mb-6 italic">
                "{review.text}"
              </p>
              <div>
                <p className="font-outfit font-bold text-[#143628]">{review.name}</p>
                <p className="font-manrope text-sm text-[#4A6359]">{review.location}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <a 
            href={COMPARION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#143628] font-manrope font-medium hover:text-[#C45A41] transition-colors"
            data-testid="reviews-read-more"
          >
            Read All 74 Reviews on Comparion
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

const ContactForm = ({ area }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    insurance_type: "Life Insurance",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send to Formspree (email to geokath11@gmail.com)
      const formspreeResponse = await fetch("https://formspree.io/f/mqeglkww", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          insurance_type: formData.insurance_type,
          message: formData.message || "No message provided",
          area: area || "Homepage",
          _subject: `New Insurance Lead: ${formData.name} - ${formData.insurance_type}${area ? ` (${area})` : ""}`
        })
      });

      // Also save to database as backup
      await axios.post(`${API}/leads`, formData);
      
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", insurance_type: "Life Insurance", message: "" });
      
      // Redirect to Comparion after 2 seconds
      setTimeout(() => {
        window.open(COMPARION_URL, "_blank");
      }, 2000);
    } catch (error) {
      console.error("Error submitting lead:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 [&>*]:min-w-0">
          {/* Contact Form */}
          <div>
            <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
              Get Your Free Quote
            </span>
            <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight mb-6" data-testid="contact-h2">
              Request a Free Consultation
            </h2>
            <p className="font-manrope text-[#4A6359] leading-relaxed mb-8">
              Fill out the form below and Kenneth will contact you within 24 hours to discuss your insurance needs. Get personalized quotes for life insurance in {area || "Elk Grove, Natomas, and Sacramento"}.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-capture-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block font-manrope font-medium text-[#143628] mb-2">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#143628]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#C45A41] focus:border-transparent font-manrope"
                    placeholder="John Smith"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block font-manrope font-medium text-[#143628] mb-2">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#143628]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#C45A41] focus:border-transparent font-manrope"
                    placeholder="john@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block font-manrope font-medium text-[#143628] mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#143628]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#C45A41] focus:border-transparent font-manrope"
                    placeholder="(916) 555-0123"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label htmlFor="insurance_type" className="block font-manrope font-medium text-[#143628] mb-2">Insurance Type</label>
                  <select
                    id="insurance_type"
                    name="insurance_type"
                    value={formData.insurance_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#143628]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#C45A41] focus:border-transparent font-manrope"
                    data-testid="input-insurance-type"
                  >
                    <option value="Life Insurance">Life Insurance</option>
                    <option value="Auto Insurance">Auto Insurance</option>
                    <option value="Home Insurance">Home Insurance</option>
                    <option value="Umbrella Insurance">Umbrella Insurance</option>
                    <option value="Multiple">Multiple Types</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block font-manrope font-medium text-[#143628] mb-2">Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-xl border border-[#143628]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#C45A41] focus:border-transparent font-manrope resize-none"
                  placeholder="Tell us about your insurance needs..."
                  data-testid="input-message"
                ></textarea>
              </div>

              {submitStatus === "success" && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl font-manrope" data-testid="success-message">
                  Thank you! Your request has been submitted. Redirecting you to Kenneth's page...
                </div>
              )}

              {submitStatus === "error" && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl font-manrope" data-testid="error-message">
                  Sorry, there was an error. Please try again or call us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C45A41] text-white px-8 py-4 rounded-full font-manrope font-semibold text-lg hover:bg-[#A84D37] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Get My Free Quote
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Google Maps & Contact Info */}
          <div className="space-y-8">
            <div className="rounded-3xl overflow-hidden shadow-xl h-[300px] md:h-[350px]" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3126.367289538!2d-121.37891492393652!3d38.41875997184392!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809ad7f7c5f8e8ed%3A0x4f5d3a2a5c8a2d8a!2s9381%20E%20Stockton%20Blvd%20%23120%2C%20Elk%20Grove%2C%20CA%2095624!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Kenneth Carson Office Location - Elk Grove, CA"
              ></iframe>
            </div>

            <div className="bg-[#143628] rounded-3xl p-8 text-white">
              <h3 className="font-outfit font-bold text-xl mb-6">Contact Information</h3>
              <div className="space-y-4">
                <a href="tel:+19165051000" className="flex items-center gap-4 font-manrope text-white/90 hover:text-white transition-colors" data-testid="contact-phone">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60">Phone</p>
                    <p className="font-medium">(916) 505-1000</p>
                  </div>
                </a>
                <a href="mailto:Kenneth.Carson@Comparion.com" className="flex items-center gap-4 font-manrope text-white/90 hover:text-white transition-colors" data-testid="contact-email">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60">Email</p>
                    <p className="font-medium">Kenneth.Carson@Comparion.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 font-manrope text-white/90">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60">Address</p>
                    <p className="font-medium">9381 E Stockton Blvd, Ste 120<br />Elk Grove, CA 95624</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 font-manrope text-white/90">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60">Business Hours</p>
                    <p className="font-medium">Mon - Fri: 8:30 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#143628] text-white py-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-12 py-8 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl">Kenneth Carson</span>
            </div>
            <p className="font-manrope text-white/60 text-sm leading-relaxed">
              Comparion Executive Agent serving Elk Grove, Natomas, and Sacramento communities with life insurance and comprehensive coverage options.
            </p>
          </div>
          
          <div>
            <h3 className="font-outfit font-bold mb-4">Quick Links</h3>
            <div className="space-y-2">
              <a href="/#services" className="block font-manrope text-white/70 hover:text-white transition-colors">Services</a>
              <a href="/#about" className="block font-manrope text-white/70 hover:text-white transition-colors">About Kenneth</a>
              <a href="/#areas" className="block font-manrope text-white/70 hover:text-white transition-colors">Service Areas</a>
              <a href="/#contact" className="block font-manrope text-white/70 hover:text-white transition-colors">Contact</a>
              <Link to="/blog" className="block font-manrope text-white/70 hover:text-white transition-colors">Insurance Blog</Link>
              <a href={COMPARION_URL} target="_blank" rel="noopener noreferrer" className="block font-manrope text-white/70 hover:text-white transition-colors">Comparion Profile</a>
            </div>
          </div>

          <div>
            <h3 className="font-outfit font-bold mb-4">Insurance Resources</h3>
            <div className="space-y-2">
              <Link to="/blog/life-insurance-agent-elk-grove-ca" className="block font-manrope text-white/70 hover:text-white transition-colors text-sm">Life Insurance Elk Grove</Link>
              <Link to="/blog/life-insurance-agent-natomas-sacramento" className="block font-manrope text-white/70 hover:text-white transition-colors text-sm">Life Insurance Natomas</Link>
              <Link to="/blog/homeowners-insurance-agent-natomas-sacramento" className="block font-manrope text-white/70 hover:text-white transition-colors text-sm">Home Insurance Natomas</Link>
              <Link to="/blog/why-choose-local-insurance-agent-sacramento" className="block font-manrope text-white/70 hover:text-white transition-colors text-sm">Why Choose Local Agent</Link>
            </div>
          </div>

          {REGIONS.map((r) => (
            <div key={r.key}>
              <h3 className="font-outfit font-bold mb-4">{r.stateName} Areas</h3>
              <div className="space-y-2">
                <Link to={r.basePath} className="block font-manrope text-white/70 hover:text-white transition-colors text-sm" data-testid={`footer-${r.key}-hub`}>Life Insurance Agent {r.stateName}</Link>
                {r.cities.map((c) => (
                  <Link key={c.slug} to={`${r.basePath}/${c.slug}`} className="block font-manrope text-white/70 hover:text-white transition-colors text-sm" data-testid={`footer-${r.key}-${c.slug}`}>Life Insurance {c.name}, {r.abbr}</Link>
                ))}
              </div>
            </div>
          ))}
          
          <div>
            <h3 className="font-outfit font-bold mb-4">Contact</h3>
            <div className="space-y-3">
              <a href="tel:+19165051000" className="flex items-center gap-3 font-manrope text-white/70 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4" />
                (916) 505-1000
              </a>
              <a href="mailto:Kenneth.Carson@Comparion.com" className="flex items-center gap-3 font-manrope text-white/70 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4" />
                Kenneth.Carson@Comparion.com
              </a>
              <div className="flex items-start gap-3 font-manrope text-white/70">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>9381 E Stockton Blvd, Ste 120<br />Elk Grove, CA 95624</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-8 text-center">
          <p className="font-manrope text-white/50 text-sm">
            © {new Date().getFullYear()} Kenneth Carson, Comparion Insurance Agent. Insurance products offered through Liberty Mutual Insurance Company and its affiliates.
          </p>
          <p className="font-manrope text-white/40 text-xs mt-2">
            Life insurance agent near me Elk Grove CA | Life insurance agent near me Natomas Sacramento | Homeowners insurance agent near me Natomas Sacramento
          </p>
        </div>
      </div>
    </footer>
  );
};

// Homepage component with all sections
const HomePage = () => {
  return (
    <>
      {/* Schema.org JSON-LD for SEO/AEO/GEO */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(homepageFAQSchema)}
      </script>
      
      <Hero />
      <TrustBanner />
      <About />
      <Services />
      <ServiceAreas />
      <Testimonials />
      <BlogPreview />
      <HomepageFAQ />
      <ContactForm />
    </>
  );
};

// Blog Preview Section for Homepage
const BlogPreview = () => {
  const featuredPosts = [
    {
      slug: "car-insurance-agent-natomas-sacramento",
      title: "Car Insurance Agent Near Me Natomas Sacramento",
      excerpt: "Find the best car insurance rates in Natomas, Sacramento. Kenneth Carson - 74 five-star reviews.",
      category: "Auto Insurance",
      featured: true
    },
    {
      slug: "life-insurance-agent-natomas-sacramento-guide",
      title: "Life Insurance Agent Near Me Natomas Sacramento",
      excerpt: "The definitive guide to finding a life insurance agent in Natomas, Sacramento.",
      category: "Life Insurance"
    },
    {
      slug: "best-insurance-agent-sacramento-county-2026",
      title: "Best Insurance Agent in Sacramento County 2026",
      excerpt: "Kenneth Carson is rated one of the best insurance agents with 74 five-star reviews.",
      category: "Insurance Tips"
    },
    {
      slug: "homeowners-insurance-agent-natomas-sacramento",
      title: "Homeowners Insurance Agent in Natomas Sacramento",
      excerpt: "Protect your Natomas Sacramento home with the right insurance coverage.",
      category: "Home Insurance"
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
            Insurance Resources
          </span>
          <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight mb-4" data-testid="blog-preview-h2">
            Insurance Tips & Guides
          </h2>
          <p className="font-manrope text-[#4A6359] max-w-2xl mx-auto">
            Expert advice on finding the right life insurance agent near me in Elk Grove CA, Natomas Sacramento, and more.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredPosts.map((post) => (
            <Link 
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="bg-white rounded-3xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-[#143628]/5 group"
              data-testid={`blog-preview-${post.slug}`}
            >
              <span className="inline-block bg-[#EBEAE5] text-[#4A6359] px-3 py-1 rounded-full text-xs font-manrope mb-4">
                {post.category}
              </span>
              <h3 className="font-outfit font-bold text-lg text-[#143628] mb-3 group-hover:text-[#C45A41] transition-colors">
                {post.title}
              </h3>
              <p className="font-manrope text-[#4A6359] text-sm leading-relaxed mb-4">
                {post.excerpt}
              </p>
              <span className="inline-flex items-center gap-1 text-[#C45A41] font-manrope font-medium text-sm">
                Read More <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 bg-[#143628] text-white px-8 py-4 rounded-full font-manrope font-medium hover:bg-[#1e4a38] transition-all duration-300"
            data-testid="view-all-posts"
          >
            <BookOpen className="w-5 h-5" />
            View All Articles
          </Link>
        </div>
      </div>
    </section>
  );
};

// Homepage FAQ Section - Critical for AEO
const HomepageFAQ = () => {
  const faqs = [
    {
      question: "Who is the best life insurance agent near me in Elk Grove, CA?",
      answer: "Kenneth Carson is a top-rated life insurance agent in Elk Grove, CA with a 5.0 rating from 74 reviews. Located at 9381 E Stockton Blvd, Suite 120, he offers personalized life insurance, auto, home, and umbrella coverage. As a former Claims Adjuster, he brings unique expertise to help families find the right coverage. Contact him at (916) 505-1000."
    },
    {
      question: "Is there a life insurance agent near me in Natomas Sacramento?",
      answer: "Yes! Kenneth Carson serves Natomas Sacramento residents with comprehensive life insurance services. He offers virtual consultations and phone appointments for Natomas families. His office is in nearby Elk Grove at 9381 E Stockton Blvd, and he can be reached at (916) 505-1000 or Kenneth.Carson@Comparion.com."
    },
    {
      question: "Where can I find a homeowners insurance agent near me in Natomas Sacramento?",
      answer: "Kenneth Carson provides homeowners insurance for Natomas Sacramento residents. As a Comparion Insurance agent, he helps homeowners find comprehensive coverage at competitive rates, with expertise in the local Sacramento market. Contact him at (916) 505-1000 for a free quote."
    },
    {
      question: "What types of insurance does Kenneth Carson offer?",
      answer: "Kenneth Carson offers life insurance (term, whole life, universal), auto insurance, homeowners insurance, and umbrella insurance. He serves clients in Elk Grove, Natomas, and the greater Sacramento area. As a Comparion Executive Agent, he can compare rates from multiple carriers."
    },
    {
      question: "How do I get a free insurance quote from Kenneth Carson?",
      answer: "You can get a free insurance quote by calling (916) 505-1000, emailing Kenneth.Carson@Comparion.com, or filling out the contact form on this website. Kenneth offers free, no-obligation consultations for life insurance, home insurance, and more."
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-[#EBEAE5]" id="faq">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
            Common Questions
          </span>
          <h2 className="font-outfit font-[850] text-3xl sm:text-4xl text-[#143628] tracking-tight" data-testid="faq-h2">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
              itemScope 
              itemType="https://schema.org/Question"
              itemProp="mainEntity"
              data-testid={`faq-item-${index}`}
            >
              <h3 className="font-outfit font-bold text-lg text-[#143628] mb-3" itemProp="name">
                {faq.question}
              </h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p className="font-manrope text-[#4A6359] leading-relaxed" itemProp="text">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const renderAreaForm = (area) => <ContactForm area={area} />;

function App() {
  return (
    <div className="App min-h-screen bg-[#F7F7F5]">
      <BrowserRouter>
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {REGIONS.map((r) => (
              <Route key={r.key} path={r.basePath} element={<StateHub region={r} renderContactForm={renderAreaForm} />} />
            ))}
            {REGIONS.map((r) => (
              <Route key={`${r.key}-city`} path={`${r.basePath}/:city`} element={<CityPage region={r} renderContactForm={renderAreaForm} />} />
            ))}
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
